import { useEffect, useRef, useState } from 'react'
import { Box, Text } from '@mantine/core'
import {
  Akm,
  AkmDrumMag,
  AkmNoMagazine,
  AkmStripped
} from '../../components/BuildsSVGs/akm'
import type { AkIllustrationVariant } from './akIllustration'
import { platformIsAkSchematic, platformIsArSchematic } from './zoneCategoryMap'
import ar15Raw from './schematics/ar15.svg?raw'
import akRaw from './schematics/ak.svg?raw'
import akZonesRaw from './schematics/ak-zones.svg?raw'
import boltActionRaw from './schematics/bolt-action.svg?raw'
import pistolRaw from './schematics/pistol.svg?raw'
import pccRaw from './schematics/pcc.svg?raw'
import shotgunRaw from './schematics/shotgun.svg?raw'
import dmrRaw from './schematics/dmr.svg?raw'
import otherRaw from './schematics/other.svg?raw'

/** Map platform string keywords → raw SVG string. */
function pickSchematicRaw (platform: string | null | undefined): string {
  if (!platform) return otherRaw
  const p = platform.toLowerCase()
  if (platformIsArSchematic(platform)) return ar15Raw
  if (platformIsAkSchematic(platform)) return akRaw
  if (p.includes('pcc') || p.includes('pistol caliber') || p.includes('carbine') || p.includes('9mm')) { return pccRaw }
  if (p.includes('pistol') || p.includes('handgun') || p.includes('semi-auto pistol')) { return pistolRaw }
  if (p.includes('shotgun') || p.includes('pump') || p.includes('semi-auto shotgun')) { return shotgunRaw }
  if (p.includes('dmr') || p.includes('designated marksman') || p.includes('precision')) { return dmrRaw }
  if (p.includes('bolt') || p.includes('bolt-action') || p.includes('hunting')) { return boltActionRaw }
  return otherRaw
}

interface Props {
  platform: string | null | undefined;
  activeZones: string[];
  /** Zone to briefly pulse (amber flash) after a part is added. */
  pulsedZone?: string | null;
  editMode?: boolean;
  /** Called when user clicks a zone. In owner mode all zones; visitor mode populated only. */
  onZoneClick?: (zone: string) => void;
  /** Zone currently selected (panel/modal is open) — receives persistent amber glow. */
  activeZone?: string | null;
  /** Part counts per zone, used for visitor-mode tooltip label. */
  zoneParts?: Record<string, number>;
  /**
   * When set (AK builds), the svgmaker-generated AKM TSX illustration (Akm, AkmStripped, etc.)
   * is the visible artwork; the overlay is transparent zone hit targets only (not the full ak.svg schematic).
   */
  akIllustrationVariant?: AkIllustrationVariant;
  /**
   * Optional ref forwarded to the inner SVG container div.
   * Lets the parent (BuildWorkbench) run useZoneGeometry on the SVG DOM.
   */
  containerRefOut?: React.RefObject<HTMLDivElement>;
}

export default function PlatformSchematic ({
  platform,
  activeZones,
  pulsedZone,
  editMode = false,
  onZoneClick,
  activeZone,
  zoneParts = {},
  akIllustrationVariant,
  containerRefOut
}: Props) {
  /** Ref on the raw SVG container — used for all querySelectorAll / style manipulation. */
  const containerRef = useRef<HTMLDivElement>(null)
  // Callback ref: writes both the internal containerRef and the optional containerRefOut
  // forwarded by the parent (used by useZoneGeometry to measure zone positions).
  const setContainerRef = (node: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    if (containerRefOut) {
      (containerRefOut as React.MutableRefObject<HTMLDivElement | null>).current = node
    }
  }
  /** Ref on the position:relative wrapper — tooltip is positioned relative to this. */
  const wrapperRef = useRef<HTMLDivElement>(null)
  /** Kept in a ref so hover-effect closures never go stale. */
  const activeZoneRef = useRef<string | null>(activeZone ?? null)

  const baseSchematicRaw = pickSchematicRaw(platform)
  /** AK builds: TSX illustration is the artwork; only zone hit targets layer on top (not duplicate ak.svg line art). */
  const interactiveOverlayRaw =
    akIllustrationVariant != null && baseSchematicRaw === akRaw ? akZonesRaw : baseSchematicRaw

  const akArtLayer =
    akIllustrationVariant === 'drum'
      ? (
      <AkmDrumMag preserveAspectRatio="xMidYMid meet" />
        )
      : akIllustrationVariant === 'full'
        ? (
      <Akm preserveAspectRatio="xMidYMid meet" />
          )
        : akIllustrationVariant === 'noMagazine'
          ? (
      <AkmNoMagazine preserveAspectRatio="xMidYMid meet" />
            )
          : akIllustrationVariant === 'noMagazineStripped'
            ? (
      <AkmStripped preserveAspectRatio="xMidYMid meet" />
              )
            : null

  /** All detailed AKM illustrations (including the new svgmaker-generated AkmStripped) use the full 2048×1365 canvas ratio. */
  const akLayerAspectRatio = akIllustrationVariant ? '2048 / 1365' : undefined

  const [tooltipInfo, setTooltipInfo] = useState<{ zone: string; x: number; y: number } | null>(null)

  // Keep activeZoneRef in sync so event-listener closures always see the latest value.
  useEffect(() => {
    activeZoneRef.current = activeZone ?? null
  }, [activeZone])

  // ── Effect 1: base opacity / color / cursor / active-zone highlight ─────────
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.querySelectorAll<SVGGElement>('g[data-zone]').forEach((g) => {
      const zone = g.getAttribute('data-zone') ?? ''
      const isActive = activeZones.includes(zone)
      const isSelected = zone === activeZone

      g.style.transition = 'all 0.15s ease'
      g.style.opacity = isActive ? '1' : '0.28'
      g.style.color = isActive ? 'var(--gs-accent)' : 'currentColor'
      // Persistent glow for the zone whose panel is currently open.
      g.style.filter = isSelected ? 'drop-shadow(0 0 8px var(--gs-accent))' : ''
      // Owner: all zones clickable. Visitor: only populated zones.
      g.style.cursor = editMode || isActive ? 'pointer' : 'default'
    })
  }, [activeZones, editMode, interactiveOverlayRaw, activeZone, akIllustrationVariant])

  // ── Effect 2: pulse animation ────────────────────────────────────────────────
  useEffect(() => {
    if (!pulsedZone) return
    const el = containerRef.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const g = el.querySelector<SVGGElement>(`g[data-zone="${CSS.escape(pulsedZone)}"]`)
    if (!g) return

    g.style.animation = 'zone-pulse 0.45s ease-out'
    const handle = setTimeout(() => { g.style.animation = '' }, 450)
    return () => clearTimeout(handle)
  }, [pulsedZone])

  // ── Effect 3: click handlers ─────────────────────────────────────────────────
  // Owner mode → all zones. Visitor mode → populated zones only.
  useEffect(() => {
    if (!onZoneClick) return
    const el = containerRef.current
    if (!el) return
    const cleanups: Array<() => void> = []

    el.querySelectorAll<SVGGElement>('g[data-zone]').forEach((g) => {
      const zone = g.getAttribute('data-zone') ?? ''
      const isActive = activeZones.includes(zone)
      if (!editMode && !isActive) return // visitors only click populated zones

      const handler = () => onZoneClick(zone)
      g.addEventListener('click', handler)
      cleanups.push(() => g.removeEventListener('click', handler))
    })

    return () => cleanups.forEach((fn) => fn())
  }, [editMode, onZoneClick, activeZones, interactiveOverlayRaw, akIllustrationVariant])

  // ── Effect 4: hover handlers ─────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current
    const wEl = wrapperRef.current
    if (!el) return
    const cleanups: Array<() => void> = []

    el.querySelectorAll<SVGGElement>('g[data-zone]').forEach((g) => {
      const zone = g.getAttribute('data-zone') ?? ''
      const isActive = activeZones.includes(zone)

      if (editMode) {
        // ── Owner mode ──────────────────────────────────────────────────────
        if (!isActive) {
          // Empty zone: dim-up on enter, dim-down on leave.
          const onEnter = () => {
            if (zone === activeZoneRef.current) return
            g.style.opacity = '0.45'
          }
          const onLeave = () => {
            if (zone === activeZoneRef.current) return
            g.style.opacity = '0.28'
          }
          g.addEventListener('mouseenter', onEnter)
          g.addEventListener('mouseleave', onLeave)
          cleanups.push(
            () => g.removeEventListener('mouseenter', onEnter),
            () => g.removeEventListener('mouseleave', onLeave)
          )
        } else {
          // Populated zone: amber drop-shadow glow.
          const onEnter = () => {
            if (zone === activeZoneRef.current) return
            g.style.filter = 'drop-shadow(0 0 6px var(--gs-accent))'
          }
          const onLeave = () => {
            if (zone === activeZoneRef.current) return
            g.style.filter = ''
          }
          g.addEventListener('mouseenter', onEnter)
          g.addEventListener('mouseleave', onLeave)
          cleanups.push(
            () => g.removeEventListener('mouseenter', onEnter),
            () => g.removeEventListener('mouseleave', onLeave)
          )
        }
      } else {
        // ── Visitor mode ────────────────────────────────────────────────────
        if (!isActive) return // empty zones: no interaction

        // Populated zone: gold tint + tooltip.
        const onEnter = (e: Event) => {
          if (zone === activeZoneRef.current) return
          g.style.opacity = '0.85'
          g.style.color = 'var(--gs-gold)'

          if (wEl) {
            const gRect = (e.currentTarget as SVGGElement).getBoundingClientRect()
            const wRect = wEl.getBoundingClientRect()
            setTooltipInfo({
              zone,
              x: gRect.left - wRect.left + gRect.width / 2,
              y: gRect.top - wRect.top
            })
          }
        }
        const onLeave = () => {
          if (zone === activeZoneRef.current) return
          g.style.opacity = '1'
          g.style.color = 'var(--gs-accent)'
          setTooltipInfo(null)
        }
        g.addEventListener('mouseenter', onEnter)
        g.addEventListener('mouseleave', onLeave)
        cleanups.push(
          () => g.removeEventListener('mouseenter', onEnter),
          () => g.removeEventListener('mouseleave', onLeave)
        )
      }
    })

    return () => cleanups.forEach((fn) => fn())
  }, [activeZones, editMode, interactiveOverlayRaw, akIllustrationVariant])

  return (
    <Box>
      {/* Pulse keyframe injected once */}
      <style>{`
        @keyframes zone-pulse {
          0%   { filter: brightness(1); }
          35%  { filter: brightness(2.2) drop-shadow(0 0 6px var(--gs-accent)); }
          100% { filter: brightness(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* Wrapper provides position:relative for the absolute tooltip overlay. */}
      <Box ref={wrapperRef} sx={{ position: 'relative' }}>
        <Box
          ref={setContainerRef}
          sx={{
            position: 'relative',
            width: '100%',
            ...(akLayerAspectRatio ? { aspectRatio: akLayerAspectRatio } : {})
          }}
        >
          {akArtLayer && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 0,
                pointerEvents: 'none',
                background: 'var(--gs-surface)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8,
                overflow: 'hidden',
                '& svg': {
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'contain'
                }
              }}
            >
              {akArtLayer}
            </Box>
          )}
          <Box
            dangerouslySetInnerHTML={{ __html: interactiveOverlayRaw }}
            sx={{
              position: akIllustrationVariant ? 'absolute' : 'relative',
              inset: akIllustrationVariant ? 0 : undefined,
              zIndex: 1,
              width: '100%',
              height: akIllustrationVariant ? '100%' : undefined,
              background: akIllustrationVariant ? 'transparent' : 'var(--gs-surface)',
              border: akIllustrationVariant ? 'none' : '1px solid var(--gs-border)',
              borderRadius: 8,
              overflow: 'hidden',
              color: 'var(--gs-muted)',
              '& svg': {
                width: '100%',
                height: 'auto',
                display: 'block',
                ...(akIllustrationVariant
                  ? { height: '100%', objectFit: 'contain' }
                  : {})
              },
              // Updated to "all" so filter + color transitions are included.
              '& svg g[data-zone]': { transition: 'all 0.15s ease' }
            }}
          />
        </Box>

        {/* Visitor-mode zone tooltip (Mantine Tooltip can't wrap raw SVG groups). */}
        {tooltipInfo && !editMode && (
          <Box
            style={{
              position: 'absolute',
              left: tooltipInfo.x,
              top: tooltipInfo.y - 6,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              zIndex: 10,
              background: '#1C2128',
              border: '1px solid var(--gs-border)',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 11,
              color: 'var(--gs-text)',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              fontFamily: 'DM Sans, sans-serif'
            }}
          >
            {tooltipInfo.zone} • {zoneParts[tooltipInfo.zone] ?? 0} part(s)
            {/* Arrow */}
            <Box
              style={{
                position: 'absolute',
                bottom: -5,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '5px solid #1C2128'
              }}
            />
          </Box>
        )}
      </Box>

      {/* Zone legend — only show populated zones */}
      {activeZones.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 12px',
            marginTop: 8,
            paddingX: 4
          }}
        >
          {activeZones.map((zone) => (
            <Text
              key={zone}
              size="xs"
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--gs-accent)',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              <span style={{ fontSize: 8, lineHeight: 1 }}>●</span>
              {zone}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  )
}
